import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import InvestmentTooltip from "Components/InvestmentTooltip/investment_tooltip_presenter";
import { gql, useMutation } from "@apollo/client";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Tooltip, IconButton } from "@mui/material";

export const REQUEST_ACCESS = gql(/* GraphQL */ `
  mutation addInvestmentAccessRequest($input: InvestmentAccessRequestInput!) {
    addInvestmentAccessRequest(input: $input) {
      id
    }
  }
`);

// Helper to manage local storage
const getStoredRequestStatus = (investmentId: number) => {
  const key = `access_requested_${investmentId}`;
  const storedValue = localStorage.getItem(key);
  if (!storedValue) return false;

  // Check if request is older than a week
  const timestamp = JSON.parse(storedValue).timestamp;
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  if (Date.now() - timestamp > oneWeek) {
    localStorage.removeItem(key);
    return false;
  }
  return true;
};

const setStoredRequestStatus = (investmentId: number) => {
  const key = `access_requested_${investmentId}`;
  localStorage.setItem(key, JSON.stringify({ timestamp: Date.now() }));
};
const InvestmentLink = ({
  investmentId,
  className,
  children,
  isEntitled,
  hasRequestedAccess = false,
}: {
  investmentId: number;
  className?: string;
  children: React.ReactNode;
  isEntitled: boolean;
  hasRequestedAccess?: boolean;
}): JSX.Element => {
  const linkClassName = classNames(className, { "fund-link": isEntitled });
  const [accessRequested, setAccessRequested] = useState(
    hasRequestedAccess || getStoredRequestStatus(investmentId)
  );

  const [requestAccess, { loading }] = useMutation(REQUEST_ACCESS, {
    variables: { input: { investmentId } },
    onCompleted: () => {
      setAccessRequested(true);
      setStoredRequestStatus(investmentId);
    },
  });

  const handleRequestAccess = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!accessRequested) {
      requestAccess();
    }
  };

  return (
    <div draggable="false" className="fund-link-wrapper">
      {!isEntitled && (
        <Tooltip
          placement="top"
          title={
            accessRequested
              ? "Access has been requested"
              : "Click to request access"
          }
        >
          <div
            onClick={!accessRequested ? handleRequestAccess : undefined}
            style={{
              cursor: accessRequested ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              // More distinct color for better visual indication
              color: accessRequested ? "#7a7a7a" : "#ff6b00", // Changed from "inherit" and "#0066cc"
            }}
          >
            <>
              <span>{children}</span>
              <span style={{ marginLeft: "5px", display: "inline-flex" }}>
                {accessRequested ? (
                  <CheckCircleOutlineIcon fontSize="small" color="disabled" />
                ) : (
                  <VisibilityOffIcon fontSize="small" color="action" />
                )}
              </span>
            </>
          </div>
        </Tooltip>
      )}

      {isEntitled && (
        <InvestmentTooltip investmentId={investmentId}>
          <Link className={linkClassName} to={`/investments/${investmentId}`}>
            {children}
          </Link>
        </InvestmentTooltip>
      )}
    </div>
  );
};

export default InvestmentLink;
